import { useRef } from 'react';
import { isPortaledEvent } from '../utils/event.js';
import { isFocusLeaving, isFocusWithin, getFirstAndLastTabbable } from '../utils/focus.js';
import { LastInteraction } from '../utils/interaction.js';

/**
 * Get a props object to spread onto an element to make it a tab loop. Focus will loop around
 * inside of the element when pressing tab, but will not prevent focus from leaving the element
 * via other means (like clicking, manual `.focus()`, etc).
 */
function useTabLoop({ onKeyDown, onBlur } = {}) {
    const isHandlingBlurRef = useRef(false);
    return {
        onKeyDown(e) {
            onKeyDown?.(e);
            if (e.key !== 'Tab')
                return;
            if (isPortaledEvent(e))
                return;
            const el = e.currentTarget;
            const [first, last] = getFirstAndLastTabbable(el);
            // If there's no tabbable elements or a single tabbable element,
            // just prevent tabbing from changing anything.
            if (first === last) {
                e.preventDefault();
                // focus may not have been on this element, it could have been on a
                // non-tabbable element within the loop, so we should manually focus it
                first?.focus();
                return;
            }
            const target = e.target;
            const forwards = !e.shiftKey;
            if (target === first && !forwards) {
                last.focus();
            }
            else if (target === last && forwards) {
                first.focus();
            }
            else {
                return;
            }
            e.preventDefault();
        },
        onBlur(e) {
            onBlur?.(e);
            // Prevent re-entry to avoid infinite recursion when .focus() triggers more blur events
            if (isHandlingBlurRef.current)
                return;
            // This is a fix for safari where our tabbable logic doesn't match what the browser does.
            // Safari doesn't focus buttons and some inputs by default, so we need to check if the window
            // is being blurred by a Tab key press and if so, focus the first tabbable element.
            if (
            // As a tab loop, only deal with the Tab key
            LastInteraction.key !== 'Tab' ||
                // Check that focus is actually leaving and not just bubbling
                !isFocusLeaving(e) ||
                isPortaledEvent(e)) {
                return;
            }
            const el = e.currentTarget;
            // Also check that focus isn't already inside the element, which would happen if the
            // above logic already kept focus inside.
            if (isFocusWithin(el))
                return;
            try {
                isHandlingBlurRef.current = true;
                const forwards = !(LastInteraction.modifiers & 4 /* Modifier.SHIFT */);
                const [first, last] = getFirstAndLastTabbable(el);
                if (forwards) {
                    first?.focus();
                }
                else {
                    last?.focus();
                }
            }
            finally {
                // try/finally ensures the flag is reset to false even if an exception occurs
                isHandlingBlurRef.current = false;
            }
        },
    };
}

export { useTabLoop };
//# sourceMappingURL=use-tab-loop.js.map
